package com.evoapps.evoshift.photo

/** The formats a picked photo may have (what the platform decoders read). */
enum class ImageFormat { JPEG, PNG, GIF, WEBP, HEIF, BMP }

/** What the first bytes of a file say: its format and, when the header has them, its size. */
data class ImageInfo(val format: ImageFormat, val width: Int? = null, val height: Int? = null) {
    val pixels: Long? get() = if (width != null && height != null) width.toLong() * height else null
}

/**
 * Reads image headers and JPEG metadata without decoding pixels (SH-94), so a picked file can be
 * refused before it takes memory, and its EXIF orientation applied and its metadata (location,
 * camera, dates) never reach a decoder that might act on it.
 */
object ImageBytes {

    /** The format and size of [bytes], or null when it is not an image the app reads. */
    fun sniff(bytes: ByteArray): ImageInfo? = when {
        isJpeg(bytes) -> jpegSize(bytes).let { ImageInfo(ImageFormat.JPEG, it?.first, it?.second) }
        startsWith(bytes, 0, 0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A) && bytes.size >= 24 ->
            ImageInfo(ImageFormat.PNG, u32be(bytes, 16).toIntOrNullPositive(), u32be(bytes, 20).toIntOrNullPositive())
        ascii(bytes, 0, "GIF87a") || ascii(bytes, 0, "GIF89a") ->
            ImageInfo(ImageFormat.GIF, bytes.u16le(6), bytes.u16le(8))
        ascii(bytes, 0, "RIFF") && ascii(bytes, 8, "WEBP") -> webp(bytes)
        ascii(bytes, 4, "ftyp") && bytes.size >= 12 &&
            CharArray(4) { (bytes[8 + it].toInt() and 0xFF).toChar() }.concatToString() in HEIF_BRANDS -> ImageInfo(ImageFormat.HEIF)
        ascii(bytes, 0, "BM") && bytes.size >= 26 ->
            ImageInfo(ImageFormat.BMP, i32le(bytes, 18).takeIf { it > 0 }, i32le(bytes, 22).let { if (it < 0) -it else it }.takeIf { it > 0 })
        else -> null
    }

    fun isJpeg(bytes: ByteArray) = startsWith(bytes, 0, 0xFF, 0xD8, 0xFF)

    /**
     * The EXIF orientation of a JPEG, 1..8 (1 = as stored; 6 = rotate 90° clockwise to view, as
     * phones save portrait photos). 1 when there is no EXIF or it cannot be read.
     */
    fun jpegOrientation(bytes: ByteArray): Int {
        if (!isJpeg(bytes)) return 1
        for (segment in jpegSegments(bytes)) {
            if (segment.marker != APP1 || !ascii(bytes, segment.dataStart, "Exif\u0000\u0000")) continue
            return tiffOrientation(bytes, segment.dataStart + 6, segment.dataEnd) ?: 1
        }
        return 1
    }

    /**
     * The JPEG without its metadata segments: EXIF and XMP (APP1, with location and camera
     * details), IPTC and the other APPn blocks, comments. The colour profile (APP2 ICC_PROFILE)
     * and the image itself stay. Not a JPEG, or one that cannot be parsed: unchanged.
     */
    fun stripJpegMetadata(bytes: ByteArray): ByteArray {
        if (!isJpeg(bytes)) return bytes
        val segments = jpegSegments(bytes)
        val scanStart = segments.lastOrNull()?.takeIf { it.marker == SOS }?.start ?: return bytes
        // Byte ranges to keep: the start of image, the other segments, the scan to the end.
        val keep = buildList {
            add(0 until 2)
            segments.filter { it.marker != SOS && !isMetadata(bytes, it) }.forEach { add(it.start until it.dataEnd) }
            add(scanStart until bytes.size)
        }
        val out = ByteArray(keep.sumOf { it.last - it.first + 1 })
        var at = 0
        for (range in keep) {
            bytes.copyInto(out, at, range.first, range.last + 1)
            at += range.last - range.first + 1
        }
        return out
    }

    /** Whether a JPEG carries EXIF/XMP/IPTC or comments (what [stripJpegMetadata] removes). */
    fun hasJpegMetadata(bytes: ByteArray): Boolean =
        isJpeg(bytes) && jpegSegments(bytes).any { it.marker != SOS && isMetadata(bytes, it) }

    // --- JPEG segments ---------------------------------------------------------------------------

    private const val APP1 = 0xE1
    private const val APP2 = 0xE2
    private const val SOS = 0xDA
    private const val COM = 0xFE
    private val HEIF_BRANDS = setOf("heic", "heix", "hevc", "hevx", "heim", "heis", "mif1", "msf1", "avif")

    /** A marker segment: [start] at its 0xFF, data (after the length) from [dataStart] to [dataEnd]. */
    private class Segment(val marker: Int, val start: Int, val dataStart: Int, val dataEnd: Int)

    /** The segments up to and including the start of scan (the image data follows it). */
    private fun jpegSegments(bytes: ByteArray): List<Segment> {
        val segments = mutableListOf<Segment>()
        var pos = 2
        while (pos + 4 <= bytes.size) {
            if (bytes[pos].toInt() and 0xFF != 0xFF) break
            val marker = bytes[pos + 1].toInt() and 0xFF
            if (marker == 0xFF) { pos++; continue } // fill byte
            if (marker == 0x01 || marker in 0xD0..0xD8) { pos += 2; continue } // no length
            if (marker == 0xD9) break // end of image
            val length = bytes.u16be(pos + 2) ?: break
            if (length < 2 || pos + 2 + length > bytes.size) break
            segments += Segment(marker, pos, pos + 4, pos + 2 + length)
            if (marker == SOS) break
            pos += 2 + length
        }
        return segments
    }

    private fun isMetadata(bytes: ByteArray, segment: Segment): Boolean = when (segment.marker) {
        COM -> true
        APP2 -> !ascii(bytes, segment.dataStart, "ICC_PROFILE")
        in 0xE1..0xEF -> true
        else -> false
    }

    /** Tag 0x0112 of IFD0 in the TIFF block [start] until [end]. */
    private fun tiffOrientation(bytes: ByteArray, start: Int, end: Int): Int? {
        if (end - start < 8) return null
        val little = when {
            ascii(bytes, start, "II") -> true
            ascii(bytes, start, "MM") -> false
            else -> return null
        }
        fun u16(at: Int): Int? = if (at + 2 > end) null else if (little) bytes.u16le(at) else bytes.u16be(at)
        fun u32(at: Int): Long? = if (at + 4 > end) null else if (little) u32le(bytes, at) else u32be(bytes, at)
        if (u16(start + 2) != 42) return null
        val ifd = start + (u32(start + 4) ?: return null).toInt()
        if (ifd < start || ifd >= end) return null
        val count = u16(ifd) ?: return null
        for (i in 0 until count) {
            val entry = ifd + 2 + i * 12
            if (entry + 12 > end) return null
            if (u16(entry) == 0x0112 && u16(entry + 2) == 3) return u16(entry + 8)?.takeIf { it in 1..8 }
        }
        return null
    }

    private fun jpegSize(bytes: ByteArray): Pair<Int, Int>? {
        var pos = 2
        while (pos + 9 <= bytes.size) {
            if (bytes[pos].toInt() and 0xFF != 0xFF) return null
            val marker = bytes[pos + 1].toInt() and 0xFF
            if (marker == 0xFF) { pos++; continue }
            if (marker == 0x01 || marker in 0xD0..0xD8) { pos += 2; continue }
            if (marker == SOS || marker == 0xD9) return null
            val length = bytes.u16be(pos + 2) ?: return null
            if (marker in 0xC0..0xCF && marker != 0xC4 && marker != 0xC8 && marker != 0xCC) {
                val height = bytes.u16be(pos + 5) ?: return null
                val width = bytes.u16be(pos + 7) ?: return null
                return width to height
            }
            if (length < 2) return null
            pos += 2 + length
        }
        return null
    }

    private fun webp(bytes: ByteArray): ImageInfo {
        fun u24le(at: Int) = if (at + 3 > bytes.size) null else
            (bytes[at].toInt() and 0xFF) or ((bytes[at + 1].toInt() and 0xFF) shl 8) or ((bytes[at + 2].toInt() and 0xFF) shl 16)
        return when {
            ascii(bytes, 12, "VP8X") -> ImageInfo(ImageFormat.WEBP, u24le(24)?.plus(1), u24le(27)?.plus(1))
            ascii(bytes, 12, "VP8 ") -> ImageInfo(ImageFormat.WEBP, bytes.u16le(26)?.and(0x3FFF), bytes.u16le(28)?.and(0x3FFF))
            ascii(bytes, 12, "VP8L") && bytes.size >= 25 -> {
                val b = IntArray(4) { bytes[21 + it].toInt() and 0xFF }
                val width = 1 + (((b[1] and 0x3F) shl 8) or b[0])
                val height = 1 + (((b[3] and 0xF) shl 10) or (b[2] shl 2) or ((b[1] and 0xC0) shr 6))
                ImageInfo(ImageFormat.WEBP, width, height)
            }
            else -> ImageInfo(ImageFormat.WEBP)
        }
    }

    // --- Bytes -----------------------------------------------------------------------------------

    private fun startsWith(bytes: ByteArray, at: Int, vararg values: Int) =
        bytes.size >= at + values.size && values.indices.all { bytes[at + it].toInt() and 0xFF == values[it] }

    private fun ascii(bytes: ByteArray, at: Int, text: String) =
        at >= 0 && bytes.size >= at + text.length && text.indices.all { (bytes[at + it].toInt() and 0xFF) == text[it].code }

    private fun ByteArray.u16be(at: Int): Int? =
        if (at < 0 || at + 2 > size) null else ((this[at].toInt() and 0xFF) shl 8) or (this[at + 1].toInt() and 0xFF)

    private fun ByteArray.u16le(at: Int): Int? =
        if (at < 0 || at + 2 > size) null else (this[at].toInt() and 0xFF) or ((this[at + 1].toInt() and 0xFF) shl 8)

    private fun u32be(bytes: ByteArray, at: Int): Long =
        (0 until 4).fold(0L) { acc, i -> (acc shl 8) or (bytes[at + i].toLong() and 0xFF) }

    private fun u32le(bytes: ByteArray, at: Int): Long =
        (3 downTo 0).fold(0L) { acc, i -> (acc shl 8) or (bytes[at + i].toLong() and 0xFF) }

    private fun i32le(bytes: ByteArray, at: Int): Int = u32le(bytes, at).toInt()

    private fun Long.toIntOrNullPositive(): Int? = takeIf { it in 1..Int.MAX_VALUE }?.toInt()
}
